<script setup lang="ts">
import { breakpointsTailwind } from '@vueuse/core'
import { useUpVoteMutation } from '~~/src/composables/mutations/upvote/useUpvoteMutation'
import { useGetElementalInfosQuery } from '~~/src/composables/query/elementals/useGetElementalInfosQuery'
import { formatThousands } from '@/utils/formatting'
import type { Elemental } from '~~/src/types/elemental'

interface IProps {
  prompt: Elemental
  variant?: 'default' | 'mini'
}

const props = withDefaults(
  defineProps<IProps>(),
  {
    variant: 'default',
  },
)
const { prompt, variant } = toRefs(props)
const breakpoints = useBreakpoints(breakpointsTailwind)
const isMobile = breakpoints.smaller('md')

const buttonRef = ref<HTMLDivElement>()

const isVoting = ref(false)

const iconSize = computed(() => variant.value === 'mini' ? (isMobile.value ? '20px' : '18px') : '32px')

const votesCount = computed({
  get: () => prompt.value.nVotes,
  set: (count) => {
    prompt.value.nVotes = count
  },
})

const user = useUser()
const isAuth = computed(() => user.value !== null)
const { data: elementalInfos, isLoading: isLoadingInfos, refetch } = useGetElementalInfosQuery(
  { id: props.prompt.id, query: { fields: 'full' } },
  { enabled: isAuth },
)

const isVoted = computed(() => {
  if (isAuth.value && elementalInfos.value)
    return elementalInfos.value.isVoted

  return prompt.value.isVoted || false
})

const { mutate } = useUpVoteMutation()
const upvote = async () => {
  if (isVoting.value)
    return

  isVoting.value = true

  mutate({
    elemental: {
      ...prompt.value!,
      isVoted: isVoted.value,
    },
    elementalType: prompt.value.elementalTypeId!,
  })

  isVoting.value = false
}

const upvotePrompt = useUpdatePromptVote()
watch(upvotePrompt, () => {
  if (upvotePrompt.value?.id === prompt.value.id) {
    if (upvotePrompt.value.nVotes || upvotePrompt.value.nVotes === 0)
      votesCount.value = upvotePrompt.value.nVotes
    prompt.value.isVoted = upvotePrompt.value.state === 'added'
  }
})

watch(user, () => {
  if (user.value !== null)
    refetch()
})
</script>

<template>
  <button
    ref="buttonRef"
    flex items-center gap-1
    :pr="variant === 'mini' ? '4px' : '8px'"
    :pl="isMobile ? (variant === 'mini' ? '10px' : '16px') : (variant === 'mini' ? '10px' : '12px')"
    :h="isMobile ? (variant === 'mini' ? '34px' : '40px') : (variant === 'mini' ? '32px' : '48px')"
    :w="variant === 'default' ? 'full' : 'auto'"
    class="relative group/upvote bg-white/80
        backdrop-blur-[8px]
        rounded-full cursor-pointer duration-125 transition-colors center
        border
      "
    :class="{
      'color-soft-black hover:color-primary border-soft-gray hover:border-primary': !isVoted,
      'color-primary border-primary': isVoted,
    }"
    @click.prevent.stop="upvote()"
  >
    <Text
      v-if="variant === 'mini'"
      variant="caption"
      color="inherit"
      :style="{ fontSize: '12px' }"
    >
      <slot>
        <div flex gap-2 justify-center>
          <span>
            {{ formatThousands(votesCount || 0) }}
          </span>
        </div>
      </slot>
    </Text>
    <Heading
      v-else
      :level="isMobile ? '5' : '4'"
      color="inherit"
      :flex-grow="variant === 'default' ? '1' : '0'"
    >
      <slot>
        <div flex gap-2 justify-center whitespace-nowrap>
          <span v-if="variant === 'default'">
            {{ isVoted ? 'Upvoted' : 'Upvote' }}
          </span>
          <span>
            {{ formatThousands(votesCount || 0) }}
          </span>
        </div>
      </slot>
    </Heading>

    <div>
      <Transition name="fade" mode="out-in">
        <IconLogo v-if="isVoted" :width="iconSize" :height="iconSize" />
        <IconLogoInactive v-else :width="iconSize" :height="iconSize" />
      </Transition>
    </div>
  </button>
</template>
